import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { FORMS_API } from '@shared/constants/api.constants';
import { OFFLINE_SKIP_401_LOGOUT, OFFLINE_SYNC_REPLAY } from '@shared/constants/offline.constants';
import { FormResponseDetails } from '@shared/models/form/form.model';
import { OfflinePendingMutation } from '@shared/models/offline/offline.model';
import { NetworkStatusService } from '@shared/services/offline/network-status.service';
import { OfflineFormStateService } from '@shared/services/offline/offline-form-state.service';
import { OfflinePolicyService } from '@shared/services/offline/offline-policy.service';
import { OfflineQueueService } from '@shared/services/offline/offline-queue.service';
import { OfflineUiService } from '@shared/services/offline/offline-ui.service';

@Injectable({ providedIn: 'root' })
export class OfflineSyncService {
  private readonly http = inject(HttpClient);
  private draining = false;

  constructor(
    private readonly policy: OfflinePolicyService,
    private readonly networkStatus: NetworkStatusService,
    private readonly queue: OfflineQueueService,
    private readonly formState: OfflineFormStateService,
    private readonly ui: OfflineUiService,
  ) {
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        void this.drainPendingMutations();
      });
    }
  }

  async drainPendingMutations(): Promise<void> {
    if (!this.policy.isOfflineModeEnabled() || !this.networkStatus.isOnline() || this.draining) {
      return;
    }

    this.draining = true;
    this.ui.setSyncInProgress(true);

    try {
      const pending = await this.queue.listPending();
      for (const mutation of pending) {
        if ((mutation.type as string) === 'offlineSubmit') {
          await this.queue.remove(mutation.mutationId);
          continue;
        }

        await this.replayLegacyMutation(mutation);
      }
    } finally {
      this.draining = false;
      this.ui.setSyncInProgress(false);
    }
  }

  async syncNow(): Promise<void> {
    await this.drainPendingMutations();
  }

  private replayContext(): HttpContext {
    return new HttpContext()
      .set(OFFLINE_SYNC_REPLAY, true)
      .set(OFFLINE_SKIP_401_LOGOUT, true);
  }

  private async replayLegacyMutation(mutation: OfflinePendingMutation): Promise<void> {
    const { clientId, serviceRequestId, formCode, formId } = mutation;
    const context = this.replayContext();

    await this.formState.markSyncing(clientId, serviceRequestId, formCode);

    try {
      let serverResponse: FormResponseDetails;

      if (mutation.type === 'addFormResponse') {
        serverResponse = await firstValueFrom(
          this.http.post<FormResponseDetails>(FORMS_API.ADD_FORM_RESPONSE, {
            ...mutation.payload,
            clientMutationId: mutation.clientMutationId,
          }, { context }),
        );
      } else {
        serverResponse = await firstValueFrom(
          this.http.put<FormResponseDetails>(FORMS_API.UPDATE_FORM_RESPONSE, {
            ...mutation.payload,
            clientMutationId: mutation.clientMutationId,
          }, { context }),
        );
      }

      await this.formState.resolveAfterSync(clientId, serviceRequestId, formCode, formId, serverResponse);
      await this.queue.remove(mutation.mutationId);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Sync failed';
      await this.queue.markFailed(mutation.mutationId, message);
      await this.formState.markSyncFailed(clientId, serviceRequestId, formCode);
    }
  }
}
